import { useEffect, useRef, useState } from "react";

const SPEECH_DB = -45;
const SILENT_AFTER_S = 10;

// Reads the live mic stream through a Web Audio analyser: level in dBFS, whether voice is present, and how long it has been silent.
export function useMicLevel(stream) {
  const [state, setState] = useState({ db: -100, voice: false, silentFor: 0, muted: false, ready: false });
  const peakRef = useRef(-100);

  useEffect(() => {
    if (!stream) {
      setState((s) => ({ ...s, ready: false }));
      return undefined;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.3;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const track = stream.getAudioTracks()[0];
    let lastVoice = performance.now();
    let raf;
    let lastPush = 0;

    const resume = () => ctx.state === "suspended" && ctx.resume();
    resume();
    window.addEventListener("pointerdown", resume);

    const loop = (now) => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
      const db = 20 * Math.log10(Math.sqrt(sum / buf.length) || 1e-8);
      peakRef.current = Math.max(db, peakRef.current - 1.2);
      if (db > SPEECH_DB) lastVoice = now;
      if (now - lastPush > 80) {
        lastPush = now;
        setState({
          db: peakRef.current,
          voice: now - lastVoice < 600,
          silentFor: (now - lastVoice) / 1000,
          muted: !track || track.muted || track.readyState === "ended" || !track.enabled,
          ready: ctx.state === "running",
        });
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointerdown", resume);
      ctx.close();
    };
  }, [stream]);

  return { ...state, silentWarn: state.silentFor > SILENT_AFTER_S };
}
